<script>
import {
  mediaMethods,
  learnComputed,
  authComputed,
  authMethods,
} from '@state/helpers';
import { breakpointsMixin } from '@mixins/breakpoints-mixin.js';
import Layout from '@layouts/main.vue';
import CardResources from '@components/footer/fyf5-resources.vue';
import VideoPlayer from '@components/videos/video-player.vue';
import moment from 'moment';
const UseLargeScreen = () =>
  import(
    /* webpackChunkName: "UseLargeScreen" */ '@components/use-large-screen.vue'
  );
export default {
  page: {
    title: 'Fyf5Module',
    meta: [
      { name: 'description', content: 'Find Your First Five Module Detail.' },
    ],
  },
  components: { Layout, UseLargeScreen, CardResources, VideoPlayer },
  mixins: [breakpointsMixin],
  mounted() {},
  props: {
    module: {
      type: Object,
      required: true,
      default: () => {},
    },
  },
  data() {
    return {
      placeholder: require('@assets/images/placeholder-photo-upload.svg'),
      isLoadingHomework: false,
    };
  },
  computed: {
    ...learnComputed,
    ...authComputed,
    coverVideo() {
      return this.buildVideoUrl;
    },
    coverImg() {
      return this.module?.coreResources?.coverImageUrl
        ? this.buildImgUrl
        : this.placeholder;
    },
    buildVideoUrl() {
      const path = this.module?.path.substring(1); // to remove '/' it is extra!
      // fyf5Cohort can later be replaced with a different cohort
      return `https://${this.fyf5Cohort?.aws_bucket}.s3.amazonaws.com${this.fyf5Cohort?.aws_bucket_path}${path}/${this.module?.coreResources?.videoUrl}`.replace(
        ' ',
        '%20'
      );
    },
    buildImgUrl() {
      const path = this.module?.path.substring(1); // to remove '/' it is extra!
      // fyf5Cohort can later be replaced with a different cohort
      return `https://${this.fyf5Cohort?.aws_bucket}.s3.amazonaws.com${this.fyf5Cohort?.aws_bucket_path}${path}/${this.module?.coreResources?.coverImageUrl}`.replace(
        ' ',
        '%20'
      );
    },
    cardResources() {
      const path = this.module?.path.substring(1); // to remove '/' it is extra!
      // fyf5Cohort can later be replaced with a different cohort
      return this.module?.additionalResources.map((resource) => ({
        svgImage: require('@assets/images/icon-file-alt.png'),
        fileType: resource?.fileType,
        pdfUrl:
          resource?.fileType === 'link'
            ? resource?.url
            : `https://${this.fyf5Cohort?.aws_bucket}.s3.amazonaws.com${this.fyf5Cohort?.aws_bucket_path}${path}/${resource?.url}`.replace(
                ' ',
                '%20'
              ),
        label: resource.label,
      }));
    },
    cohortDate() {
      // for fyf5
      return `${moment(this.module?.coreResources?.date).format('MM/DD/YYYY')}`;
    },
    numOfHomeworkUpload() {
      return this.currentUser?.trainer?.cohortHomework?.filter(
        (homework) => homework.module === this.module.slug
      ).length;
    },
    videoOpts() {
      return {
        autoplay: false,
        controls: true,
        poster: this.coverImg,
        fluid: true,
        responsive: true,
        aspectRatio: '16:9',
        sources: [
          {
            src: this.coverVideo,
          },
        ],
      };
    },
  },
  methods: {
    ...mediaMethods,
    ...authMethods,
    onPickFile() {
      this.$refs.fileInput.click();
    },
    async getPresignedUrl(file) {
      try {
        await this.getS3UploadUrl({
          file,
          requestType: 'learn',
          cohortId: this.fyf5Cohort?._id,
          moduleId: this.module?.slug,
          orgId: this.fyf5Cohort?.org_id,
          type: 'pdf',
          trainerId: this.trainerId,
        });
      } catch (e) {
        console.log(e);
      }
    },
    async uploadHomework(event) {
      this.isLoadingHomework = true;
      const file = event.target.files[0];
      // request to get presigned url, then upolad the file to that url
      await this.getPresignedUrl(file);
      await this.uploadPhoto({ model: 'learn', option: 'pdf' });

      // need to validate and update the currentUser.trainer obj with the homework array
      this.SET_AUTH_CHECKED(false);
      await this.validate();

      this.isLoadingHomework = false;
    },
  },
  filters: {
    replaceWithSpace(value) {
      return value.replace('-', ' ');
    },
  },
};
</script>

<template>
  <UseLargeScreen v-if="isSmallScreenView" />
  <Layout v-else>
    <div class="container-wrapper">
      <div class="module-wrapper">
        <h1>{{ module && module.slug | replaceWithSpace }}</h1>
        <main>
          <div class="image-wrapper">
            <VideoPlayer :options="videoOpts" class="module-image" />
            <div class="caption">
              <span class="description">{{ module && module.label }}</span>
              <span class="date">{{ cohortDate }}</span>
            </div>
          </div>
          <div class="article">
            <h5>Summary</h5>
            <p>{{ module && module.description }}</p>
            <BaseButton
              type="button"
              class="btn-primary btn-round upload-btn"
              @click.stop="onPickFile"
            >
              <svg width="15" height="17" viewBox="0 0 15 17" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:class="'upload-icon'" v-if="!isLoadingHomework" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M10.5 7v6h-6V7h-4l7-7 7 7h-4zm4 10v-2H.5v2h14z" fill="#000"/></svg>
              <BaseIcon v-if="isLoadingHomework" name="sync" spin />
              <span v-if="!isLoadingHomework"
                >upload <span v-if="isDesktopView">Worksheet</span></span
              >
            </BaseButton>
            <input
              type="file"
              name="uploadfile"
              ref="fileInput"
              :style="{ display: 'none' }"
              @change.prevent="uploadHomework($event)"
            />
            <p v-show="numOfHomeworkUpload" class="complete-message"
              >{{ numOfHomeworkUpload }} documents uploaded</p
            >
          </div>
        </main>
        <div class="footer">
          <p class="footer-title" v-if="cardResources.length"
            >Other Resources</p
          >
          <div class="grid-cards">
            <CardResources
              v-for="item in cardResources"
              v-bind="item"
              :key="item._id"
            />
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-pseudo-element-no-unknown

.container-wrapper {
  background: $color-cohort-main-backgraound;
  flex-direction: column;
  justify-content: space-between;

  .module-wrapper {
    padding: 5em 3em;

    main {
      display: flex;
      justify-content: space-between;

      .image-wrapper {
        width: 75%;
        .module-image {
          border-radius: $base-border-radius-lg;
          object-fit: cover;
          width: 100%;
        }
        .caption {
          display: flex;
          justify-content: space-between;

          .description {
            color: $color-black-one;
            font-size: 21px;
            font-weight: 900;
            letter-spacing: -0.3px;
            line-height: 24px;
          }

          .date {
            color: $color-black;
            font-size: 17px;
            letter-spacing: -0.2px;
            line-height: 17px;
            opacity: 0.5;
          }
        }
      }

      .article {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        margin-right: 2em;
        width: 20%;

        h5 {
          color: $color-dark-gray-one;
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 0.1px;
          line-height: 22px;
          text-transform: uppercase;
        }

        p {
          color: $color-black;
          font-size: 17px;
          font-weight: normal;
          letter-spacing: -0.2px;
          line-height: 28px;
          margin-bottom: 2em;
        }

        .upload-btn {
          align-self: center;
          padding: $size-button-padding;
          width: 70%;

          @media (max-width: 1430px) {
            width: 100%;
          }

          .upload-icon {
            margin-right: 0.5em;
            path {
              fill: $color-white;
            }
          }
        }
        .complete-message {
          margin-top: 20px;
        }
      }
    }

    .footer {
      position: relative;
    }
    .footer-title {
      color: $color-dark-gray-one;
      font-size: 12px;
      font-weight: 800;
      letter-spacing: 0.1px;
      line-height: 22px;
      margin: 3em 0 1em 0;
      text-transform: uppercase;
    }
    .grid-cards {
      @include grid-cards(1, 28);
    }
  }

  ::v-deep .video-js {
    border-radius: $base-border-radius-md * 2;

    .vjs-tech {
      border-radius: $base-border-radius-md * 2;
    }
    .vjs-control-bar {
      border-bottom-left-radius: $base-border-radius-md * 2;
      border-bottom-right-radius: $base-border-radius-md * 2;
    }

    .vjs-poster {
      background-size: cover;
      border-radius: $base-border-radius-md * 2;
    }
  }
}
</style>
